import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import { LookupField, SkuTextField } from "../components/Products/TypedFields.jsx";
import {
    catalogDangerButtonSx,
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "../components/Products/catalogGridStyles.js";
import useApiData from "../services/useApiData.js";
import PostData from "../services/PostData.js";
import PutData from "../services/PutData.js";
import api from "../services/axiosInterceptor.js";
import SnackbarEx from "../components/Helpers/SnackbarEx.jsx";
import ConfirmDeleteDialog from "../components/Helpers/ConfirmDeleteDialog.jsx";
import GridFilterBar, { useGridFilter } from "../components/Helpers/GridFilterBar.jsx";
import { SHELF_FILTER_FIELDS } from "../components/Helpers/gridFilterFields.js";

const ENDPOINT = "broylesllc/shelf-configs";
const MIN_SEARCH = 2;

function itemIdLabel(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value.text || value.name || "";
    return String(value);
}

function catalogOption(record) {
    if (!record?.id) return null;
    return {
        id: record.id,
        itemId: itemIdLabel(record.itemId) || record.id,
        skuName: record.skuName || "",
        photo: record.photo || null,
    };
}

function newBinRow(name = "", id = null) {
    return {
        key: id || `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id,
        name,
    };
}

function binsFromShelf(shelf) {
    return (shelf?.bins || []).map((bin) => newBinRow(bin.name || "", bin.id || null));
}

export default function ShelfConfigs() {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [shelfName, setShelfName] = useState("");
    const [bins, setBins] = useState([]);
    const [linkedItems, setLinkedItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");
    const [debouncedItemSearch, setDebouncedItemSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const { apply: applyFilter, filterActive, barProps: filterBar } =
        useGridFilter(SHELF_FILTER_FIELDS);

    const selectedIds = useMemo(() => [...(selectionModel?.ids ?? [])], [selectionModel]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedItemSearch(itemSearch.trim()), 300);
        return () => clearTimeout(timer);
    }, [itemSearch]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(filterActive ? 1 : paginationModel.page + 1),
            pageSize: String(filterActive ? 500 : paginationModel.pageSize),
        });
        if (searchQ) params.set("q", searchQ);
        return `api/${ENDPOINT}?${params.toString()}`;
    }, [paginationModel.page, paginationModel.pageSize, searchQ, filterActive]);

    const { data, error, load, refetch } = useApiData(listUrl);

    const detailUrl = editingId ? `api/${ENDPOINT}/${editingId}` : `api/${ENDPOINT}/none`;
    const { data: detail, load: detailLoading, error: detailError } = useApiData(detailUrl, {
        enabled: Boolean(dialogOpen && editingId),
    });

    const catalogUrl = `api/broylesllc/shelf-inventory/item-id-catalog?search=${encodeURIComponent(debouncedItemSearch)}&limit=100`;
    const { data: catalogData, load: catalogLoading } = useApiData(catalogUrl, {
        enabled: dialogOpen && debouncedItemSearch.length >= MIN_SEARCH,
    });

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : items.length);
        setRows(items);
    }, [data]);

    const filteredRows = useMemo(() => applyFilter(rows), [rows, applyFilter]);

    useEffect(() => {
        if (filterActive) {
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }
    }, [filterActive]);

    const catalogOptions = useMemo(() => {
        const byId = new Map(linkedItems.map((item) => [item.id, item]));
        for (const record of catalogData || []) {
            const option = catalogOption(record);
            if (option && !byId.has(option.id)) byId.set(option.id, option);
        }
        return [...byId.values()];
    }, [catalogData, linkedItems]);

    const editingRow = rows.find((row) => row.id === editingId);
    const detailMatches = detail?.id === editingId ? detail : null;
    const appliedDetailId = useRef(null);

    useEffect(() => {
        if (!dialogOpen) appliedDetailId.current = null;
    }, [dialogOpen]);

    useEffect(() => {
        if (!detailMatches || appliedDetailId.current === editingId) return;
        appliedDetailId.current = editingId;
        setLinkedItems((detailMatches.itemIdCatalog || []).map((item) => catalogOption(item)).filter(Boolean));
        setBins(binsFromShelf(detailMatches));
    }, [detailMatches, editingId]);

    const columns = useMemo(
        () => [
            { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
            { field: "shelfName", headerName: "Shelf Name", flex: 1, minWidth: 140 },
            {
                field: "binNames",
                headerName: "Bins",
                flex: 1,
                minWidth: 160,
                valueGetter: (_value, row) => row.binNames || "",
            },
            { field: "binCount", headerName: "Bin count", width: 110, type: "number" },
            { field: "itemCount", headerName: "Items", width: 100, type: "number" },
            { field: "created", headerName: "Created", width: 120 },
        ],
        []
    );

    const openCreate = () => {
        setEditingId(null);
        setShelfName("");
        setBins([]);
        setLinkedItems([]);
        setItemSearch("");
        setDialogOpen(true);
    };

    const openEdit = (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        const row = rows.find((item) => item.id === id);
        if (!row) return;
        setEditingId(id);
        setShelfName(row.shelfName || "");
        setBins(binsFromShelf(row));
        setLinkedItems([]);
        setItemSearch("");
        setDialogOpen(true);
    };

    const updateBinName = (key, name) => {
        setBins((prev) => prev.map((bin) => (bin.key === key ? { ...bin, name } : bin)));
    };

    const removeBin = (key) => {
        setBins((prev) => prev.filter((bin) => bin.key !== key));
    };

    const addBin = () => {
        setBins((prev) => [...prev, newBinRow()]);
    };

    const save = async () => {
        const name = shelfName.trim();
        if (!name) {
            setSnack({ open: true, color: "error", message: "Shelf Name is required." });
            return;
        }
        const namedBins = bins
            .map((bin) => ({
                ...(bin.id ? { id: bin.id } : {}),
                name: String(bin.name || "").trim(),
            }))
            .filter((bin) => bin.name);
        const names = namedBins.map((bin) => bin.name.toLowerCase());
        if (new Set(names).size !== names.length) {
            setSnack({ open: true, color: "error", message: "Bin names must be unique on a shelf." });
            return;
        }
        setSaving(true);
        const payload = {
            shelfName: name,
            bins: namedBins,
            itemIdCatalog: linkedItems.map((item) => item.id),
        };
        const result = editingId
            ? await PutData(payload, `${ENDPOINT}/${editingId}`)
            : await PostData(payload, ENDPOINT);
        setSaving(false);
        if (result.success) {
            setDialogOpen(false);
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Shelf updated." : "Shelf created.",
            });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } else {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Save failed.",
            });
        }
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id || row._id)))
            .map((row) => ({
                key: String(row.id || row._id),
                label: row.shelfName || String(row.id || row._id),
            }));
    }, [rows, selectedIds]);

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`api/${ENDPOINT}/${id}`)));
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <CatalogPageShell
            title="Shelf Configs"
            subtitle="Open a shelf to see the catalog items on it."
        >
            {error ? (
                <Box sx={{ px: 2, py: 1, color: "#b91c1c", fontSize: 13 }}>Failed to load shelves.</Box>
            ) : null}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search shelves…"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">{selectedIds.length} selected</span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load)}
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        onRowClick={(params, event) => {
                            const target = event?.target;
                            if (target?.closest?.("input, .MuiCheckbox-root, button")) return;
                            openEdit(params.id);
                        }}
                        onRowDoubleClick={(params) => openEdit(params.id)}
                        paginationMode={filterActive ? "client" : "server"}
                        rowCount={filterActive ? filteredRows.length : rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={appDataGridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingId ? "Edit Shelf" : "Add Shelf"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        {editingId ? (
                            <LookupField label="Name" value={detailMatches?.name || editingRow?.name} source="Shelf name and created date" />
                        ) : null}
                        <SkuTextField
                            label="Shelf Name"
                            required
                            value={shelfName}
                            onChange={(event) => setShelfName(event.target.value)}
                        />
                        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: "#5c6570" }}>
                                    Bins
                                </Box>
                                <Button
                                    size="small"
                                    startIcon={<AddOutlinedIcon fontSize="small" />}
                                    onClick={addBin}
                                    sx={catalogQuietButtonSx}
                                >
                                    Add bin
                                </Button>
                            </div>
                            {bins.length === 0 ? (
                                <div className="text-muted small">
                                    No bins yet. Add one or more named bins for this shelf.
                                </div>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    {bins.map((bin, index) => (
                                        <Box
                                            key={bin.key}
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label={`Bin ${index + 1}`}
                                                placeholder="Bin name"
                                                value={bin.name}
                                                onChange={(event) =>
                                                    updateBinName(bin.key, event.target.value)
                                                }
                                            />
                                            <Button
                                                size="small"
                                                aria-label={`Remove bin ${index + 1}`}
                                                onClick={() => removeBin(bin.key)}
                                                sx={{ minWidth: 36, px: 0.5, color: "#64748b" }}
                                            >
                                                <CloseOutlinedIcon fontSize="small" />
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: "#5c6570" }}>
                                Item ID Catalog
                            </Box>
                            <Autocomplete
                                multiple
                                size="small"
                                options={catalogOptions}
                                value={linkedItems}
                                inputValue={itemSearch}
                                loading={catalogLoading}
                                filterOptions={(options) => options}
                                getOptionLabel={(option) => option?.itemId || option?.id || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_event, next) => setLinkedItems(next)}
                                onInputChange={(_event, value, reason) => {
                                    if (reason === "input" || reason === "clear") setItemSearch(value);
                                }}
                                noOptionsText={
                                    debouncedItemSearch.length < MIN_SEARCH
                                        ? `Type at least ${MIN_SEARCH} characters to search`
                                        : "No item IDs found"
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        hiddenLabel
                                        placeholder="Search item IDs"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {catalogLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        {editingId ? (
                            <>
                                <LookupField label="Created" value={detailMatches?.created || editingRow?.created} />
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="fw-semibold" style={{ fontSize: 12, color: "#5c6570" }}>
                                        Catalog items
                                    </span>
                                    {detailLoading ? <CircularProgress size={14} /> : null}
                                </div>
                                {detailError ? (
                                    <div className="text-danger small">Unable to load catalog items for this shelf.</div>
                                ) : null}
                                {detailMatches?.unresolvedCount ? (
                                    <div className="text-muted small">
                                        {detailMatches.unresolvedCount} catalog item
                                        {detailMatches.unresolvedCount === 1 ? "" : "s"} on this shelf
                                        {detailMatches.unresolvedCount === 1 ? " is" : " are"} not in the catalog database yet.
                                    </div>
                                ) : null}
                                {!detailLoading && linkedItems.length === 0 && !detailMatches?.unresolvedCount ? (
                                    <div className="text-muted small">No catalog items on this shelf.</div>
                                ) : null}
                                {linkedItems.length ? (
                                    <div className="list-group">
                                        {linkedItems.map((item) => (
                                            <div key={item.id} className="list-group-item d-flex align-items-center gap-2 px-2 py-2">
                                                {item.photo?.url ? (
                                                    <img
                                                        src={item.photo.url}
                                                        alt=""
                                                        width={40}
                                                        height={40}
                                                        style={{ objectFit: "cover", borderRadius: 6 }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="bg-light border rounded"
                                                        style={{ width: 40, height: 40, flexShrink: 0 }}
                                                    />
                                                )}
                                                <div className="min-w-0">
                                                    <div className="fw-semibold text-truncate">{item.itemId}</div>
                                                    {item.skuName ? (
                                                        <div className="text-muted small text-truncate">{item.skuName}</div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || (Boolean(editingId) && detailLoading)}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? "Delete shelf?"
                        : `Delete ${deleteTargets.length} shelves?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </CatalogPageShell>
    );
}
